import type { TExample } from '@atlas/uicorn/preview/modules';
import { useContext, useMemo } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import { Slider as AntSlider, type InputProps as TAntInputProps, type InputRef as TAntInputRef } from 'antd';
import useControlledState from '@atlas/tool/box/hooks/useControlledState';
import useRefCallback from 'use-ref-callback';
import { FormContext } from '@atlas/uicorn/lib/Control/Form';

const Range = React.memo(
    React.forwardRef<TRangeRef, TRangeProps>(function Range(props, ref): React.ReactElement {
        const {
            className,
            prefix: propsPrefix,
            suffix: propsSuffix,
            value: propsValue,
            defaultValue: propsDefaultValue,
            onChange: propsOnChange,
            disabled: propsDisabled,
            ...inputProps
        } = props;

        const { disabled: contextDisabled } = useContext(FormContext);
        const disabled = propsDisabled ?? contextDisabled;

        const [value, setValue] = useControlledState(propsValue, propsOnChange, propsDefaultValue ?? props.min ?? 0);

        const handleChange = useRefCallback((newValue: number) => setValue(newValue));

        const prefix = useMemo(() => propsPrefix && propsPrefix(value, disabled), [value, disabled, propsPrefix]);
        const suffix = useMemo(() => propsSuffix && propsSuffix(value, disabled), [value, disabled, propsSuffix]);

        return (
            <SRange className={className}>
                {prefix}
                <SRangeInput
                    {...inputProps}
                    value={value}
                    onChange={handleChange}
                    disabled={disabled}
                    ref={ref}
                    tooltip={tooltip}
                />
                {suffix}
            </SRange>
        );
    }),
);

export default Range;

// #region Helpers

const tooltip = { open: false };

// #endregion

// #region Styles

const SRangeInput = styled(AntSlider)`
    --range-input-height: 0.6rem;
    --range-input-handle-radius: 1rem;

    min-width: 2rem;
    color: ${(p) => p.theme.color.grey9};
    font-size: 1.4rem;
    line-height: 1.5715;
    font-variant: tabular-nums;
    list-style: none;
    font-feature-settings: 'tnum';
    position: relative;
    height: calc(2 * var(--range-input-height));
    margin: 0 var(--range-input-handle-radius);
    padding: var(--range-input-handle-radius) 0 calc(var(--range-input-handle-radius) + var(--range-input-height));
    cursor: pointer;
    touch-action: none;

    .ant-slider-rail,
    &:hover .ant-slider-rail {
        height: var(--range-input-height);
        border-radius: calc(var(--range-input-height) / 2);
        background-color: ${(p) => p.theme.color.grey6};
    }

    .ant-slider-track,
    &:hover .ant-slider-track {
        height: var(--range-input-height);
        border-radius: calc(var(--range-input-height) / 2);
        background-color: ${(p) => p.theme.color.grey9};
    }

    .ant-slider-step {
        height: var(--range-input-height);
    }

    .ant-slider-handle {
        width: calc(2 * var(--range-input-handle-radius));
        height: calc(2 * var(--range-input-handle-radius));
        margin-top: -0.75rem;
        background-color: ${(p) => p.theme.color.grey0};
        border: 0.1rem solid ${(p) => p.theme.color.grey6};
        box-shadow: 0px 0.4rem 2.8rem 0.6rem rgba(38, 38, 38, 0.08);
    }

    &:hover .ant-slider-handle:not(.ant-tooltip-open) {
        border-color: ${(p) => p.theme.color.primary};
    }
`;

export const SRange = styled.div`
    width: 100%;
    display: inline-flex;
    align-items: center;
    gap: 0.8rem;

    ${SRangeInput} {
        flex: 1;
    }
`;

// #endregion

// #region Types

export type TRangeProps = Pick<TAntInputProps, 'className' | 'disabled' | 'onFocus' | 'onBlur' | 'onClick'> & {
    value?: number;
    defaultValue?: number;
    onChange?: (newValue: number) => void;
    min?: number;
    max?: number;
    prefix?: (value: number, disabled: boolean) => React.ReactNode;
    suffix?: (value: number, disabled: boolean) => React.ReactNode;
};

export type TRangeRef = TAntInputRef;

// #endregion

// #region Examples

export const Example: TExample = {
    containerStyle: {
        width: 400,
    },
    render(props) {
        const prefix = useMemo(
            () =>
                props.prefix
                    ? (value: number) => (
                          <strong>
                              {value}
                              {typeof props.max === 'number' ? ` / ${props.max}` : ''}
                          </strong>
                      )
                    : undefined,
            [props.prefix],
        );

        const suffix = useMemo(
            () =>
                props.suffix
                    ? (value: number) => (
                          <strong>
                              {value}
                              {typeof props.max === 'number' ? ` / ${props.max}` : ''}
                          </strong>
                      )
                    : undefined,
            [props.suffix],
        );

        return (
            <Range
                min={props.min}
                max={props.max}
                prefix={prefix}
                suffix={suffix}
                onChange={props.onChange}
                disabled={props.disabled}
            />
        );
    },
    controls: {
        min: { type: 'number', initial: 0 },
        max: { type: 'number', initial: 10 },
        prefix: 'boolean',
        suffix: { type: 'boolean', initial: true },
        disabled: 'boolean',
    },
    handlers: {
        onChange: 'console',
    },
    figma: 'https://www.figma.com/file/YK0CuM3dK7LcMbVtTUuL8I/Product----Web-2.0?node-id=10:18263&mode=dev',
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('Range', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
